import { AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { Button } from 'base/ui/button';
import { IconAdd, IconStars } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { menuDivider, menuItem, subMenu } from 'base/ui/menu_model';
import { ClickInTextarea, ClickInTextareaRef } from 'base/ui/textarea';
import {
  TextSpecialSectionDescription,
  TextSpecialSectionName,
  TextTinyLight,
} from 'base/ui/typography';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import classNames from 'classnames/bind';
import { observer } from 'mobx-react-lite';
import { expr } from 'mobx-utils';
import React from 'react';
import { shortcut, useKeyboardShortcuts } from '../../keyboard_shortcuts';
import { NotificationsContainer, TaskNotifications } from '../ai/notifications';
import { ProjectEditingActions } from '../editing_actions/project';
import { EditorMode } from '../editor_mode';
import { sectionColorMenu } from '../edit_panel/section_sitemap';
import {
  MISSING_GLOBAL_SECTION_STATE,
  Page,
  PageState,
  SectionRef,
  SectionRefState,
} from '../model';
import { SECTION_COLOR_VALUES } from '../section_colors';
import { ADD_BELOW_ACTION_POSITION, DragHandleOpts } from '../selection/frame';
import { useSelectable } from '../selection/selectable';
import { SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { globalSuggestionsStore } from '../suggested_edits';
import styles from './sections.css';
import { useCommonSectionInteractions } from './section_common';
import {
  makeDropBeforeTarget,
  makeSectionSplitDropTarget,
  sectionDndDomain,
} from './section_dnd';

const cx = classNames.bind(styles);

export const SectionList = observer(function _PageSections({
  page,
  selection,
  selectionActions,
  analytics,
  editing,
  flags,
  showOnPageHoverClassName,
}: {
  page: Page | PageState;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  flags: FeatureFlagsStore;
  showOnPageHoverClassName: string;
}) {
  const isEmpty =
    page.sections.length === 0 &&
    !(page.mutable && globalSuggestionsStore.hasSuggestedSections(page));
  const pageOnlySelected = expr(() => {
    const sel = selection.get();
    return sel.page === page && sel.section == null;
  });

  const onClickAdd = (editing: ProjectEditingActions) => {
    if (page.mutable) {
      selection.set(selection.factory.forPage(page));
    }
    editing.sidebarNavigation.toggleToplevel('add');
  };
  return (
    <sectionDndDomain.DropTarget
      className={cx(styles.sectionList, isEmpty && styles.isEmpty)}
      opts={makeDropBeforeTarget(page, 0, editing)}
    >
      {Array.from({ length: page.sections.length + 1 }).map((_, i) => (
        <SectionListItem
          key={i}
          page={page}
          index={i}
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          editing={editing}
          flags={flags}
        />
      ))}
      {isEmpty && isSpaceHome(page, selection) && (
        <div className={styles.emptyText}>
          <TextTinyLight color="grey">
            Click the sitemap bar above to generate a full sitemap with AI
          </TextTinyLight>
        </div>
      )}
      {isEmpty && editing && page.mutable && (
        <div
          className={cx(
            styles.emptyButtons,
            showOnPageHoverClassName,
            pageOnlySelected && styles.forceShow,
          )}
        >
          <Rows spacing={8}>
            <Button
              icon={IconAdd}
              label="Section"
              onClick={() => onClickAdd(editing)}
            />
            <Button
              icon={IconStars}
              label="Generate content"
              variant="secondary"
              iconColor="ai"
              onClick={editing.forPage(page).regenerateOrOpenForm}
            />
          </Rows>
        </div>
      )}
    </sectionDndDomain.DropTarget>
  );
});

function isSpaceHome(page: Page | PageState, selection: SelectionStore) {
  if (!page.mutable) return false;
  const parent = selection.factory.treeParentOfPage(page);
  return parent.page == null;
}

/**
 * Displays an index in the section list, which consists of:
 *
 *   - any drops hovering this index
 *   - any suggestions to insert at this index
 *   - the section itself (if the index is in the list)
 */
const SectionListItem = observer(function _SectionListItem({
  page,
  selection,
  selectionActions,
  analytics,
  index,
  editing,
  flags,
}: {
  page: Page | PageState;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  index: number;
  editing: ProjectEditingActions | undefined;
  flags: FeatureFlagsStore;
}) {
  const dndState = sectionDndDomain.state.get();
  const isHoveringMe =
    dndState?.dest?.page === page && dndState.dest.index === index;
  const dropPreview = dndState && isHoveringMe && (
    <sectionDndDomain.DropTarget
      className={cx(styles.sectionWrapper, styles.containsImposter)}
      opts={makeDropBeforeTarget(page, index, editing)}
    >
      <div className={styles.sectionImposter}>
        {/**
         * We have to render the dropped content to get the right size,
         * even though we don't display it.  The user might be dragging
         * from the add panel, so the size of the drag source isn't the
         * size it'll be when dropped.
         **/}
        <SectionView
          sref={dndState.source.section}
          isSuggestion={true}
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          projectEditing={editing}
        />
      </div>
    </sectionDndDomain.DropTarget>
  );

  const suggested = page.mutable
    ? globalSuggestionsStore.getSuggestedSections(page, index)
    : [];
  const suggestedContent = suggested.map((section) => (
    <sectionDndDomain.DropTarget
      className={styles.sectionWrapper}
      key={keyOfObject(section)}
      opts={makeDropBeforeTarget(page, index, editing)}
    >
      <SectionView
        sref={section}
        isSuggestion={true}
        selection={selection}
        selectionActions={selectionActions}
        analytics={analytics}
        projectEditing={editing}
      />
    </sectionDndDomain.DropTarget>
  ));

  const numPending =
    index === page.sections.length &&
    page.mutable &&
    selection.decorations.arePageSectionsPending(page)
      ? 3
      : 0;
  const pendingContent = Array.from({ length: numPending }).map((_, j) => (
    <sectionDndDomain.DropTarget
      className={styles.sectionWrapper}
      key={j}
      opts={makeDropBeforeTarget(page, index, editing)}
    >
      <div
        className={styles.sectionPending}
        style={{ ['--index' as string]: j }}
      />
    </sectionDndDomain.DropTarget>
  ));

  const section = page.sections[index];
  let sectionContent;
  if (section) {
    const dropTarget = makeSectionSplitDropTarget(page, index, editing);
    sectionContent = (
      <sectionDndDomain.Draggable
        key={keyOfObject(section)}
        source={
          editing && section.mutable && page.mutable
            ? {
                section: section.state,
                from: { page, index, mutable: section },
              }
            : undefined
        }
        supportsDuplicate={true}
        selfDropTarget={page.mutable ? { page, index } : undefined}
        onAfterDrop={editing?.onAfterDropSection}
        dropTarget={dropTarget}
      >
        <SectionView
          sref={section}
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          projectEditing={editing}
        />
      </sectionDndDomain.Draggable>
    );
    sectionContent = (
      <sectionDndDomain.DropTarget
        className={styles.sectionWrapper}
        opts={dropTarget}
      >
        {sectionContent}
      </sectionDndDomain.DropTarget>
    );
  }
  return (
    <>
      {dropPreview}
      {suggestedContent}
      {pendingContent}
      {sectionContent}
    </>
  );
});

// This is a terrible hack to make the "Enter to add new section, then keep typing"
// behaviour work
let handoverContinueSelection: SectionRef | undefined = undefined;

const SectionView = observer(function _SectionView({
  sref,
  selection,
  selectionActions,
  analytics,
  isSuggestion,
  projectEditing,
}: {
  sref: SectionRef | SectionRefState;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  isSuggestion?: boolean;
  projectEditing: ProjectEditingActions | undefined;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const nameRef = React.useRef<ClickInTextareaRef>(null);
  const descriptionRef = React.useRef<ClickInTextareaRef>(null);

  const section =
    sref.type === 'inline'
      ? sref.value
      : selection.project.globalSections.get(sref.id) ||
        MISSING_GLOBAL_SECTION_STATE;

  const slug = section.element.current?.source.slug;
  if (slug) {
    // Preload the component JSON in case the user switches to the wireframe tab later on.
    projectEditing?.library.getPublicComponentJson(slug);
  }

  const isAdding = projectEditing?.sidebarNavigation.toplevel === 'add';

  const sectionEditing = sref.mutable
    ? projectEditing?.forSection(sref)
    : undefined;
  const selectionFrameAction =
    sectionEditing && !isAdding
      ? {
          showWhenSelected: false,
          showWhenHovering: true,
          pos: ADD_BELOW_ACTION_POSITION,
          maxWidthRatio: 0.35,
          content: (
            <div className={styles.addSectionBelow}>
              <Button
                variant="primary"
                background={sref.type === 'reference' ? 'global' : 'ai'}
                size="small"
                icon={IconAdd}
                onClick={() => {
                  // because this is an overlay, we must explicitly trigger selection
                  if (sref.mutable)
                    selection.set(selection.factory.forSection(sref), {
                      disableEnsureVisible: true,
                    });
                  projectEditing?.sidebarNavigation.toggleToplevel('add');
                }}
              />
            </div>
          ),
        }
      : undefined;
  const [isSelected, selectableClassName] = sref.mutable
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useSelectable(ref, selection, selection.factory.forSection(sref), {
        actions: { add: selectionFrameAction },
        dragHandle: sectionEditing ? DRAG_HANDLE_OPTS : undefined,
        frameColor: sref.type === 'reference' ? 'global' : undefined,
        onSelect: projectEditing?.sidebarNavigation.openEditPanelIfClosed,
      })
    : [false, undefined];

  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter')]: () => nameRef.current?.focus(),
  });

  const extraMenuItems = sectionEditing
    ? [
        subMenu({
          label: 'Change color',
          menu: sectionColorMenu({
            color: section.color,
            onChange: sectionEditing.setColor,
          }),
        }),
        menuDivider(),
        menuItem({
          label: 'Find component',
          onClick: sectionEditing.makeComponentVisible,
        }),
      ]
    : [];
  const [onContextMenu, menuPortal] = useCommonSectionInteractions({
    sectionEditing,
    projectEditing,
    selectionActions,
    analytics,
    isSelected,
    extraMenuItems,
  });

  React.useEffect(() => {
    if (handoverContinueSelection === sref) {
      nameRef.current?.focus();
      handoverContinueSelection = undefined;
    }
  }, [sref]);

  const hasName = section.name.trim().length > 0;
  const hasDescription = section.description.trim().length > 0;

  return (
    <div
      className={cx(
        styles.section,
        isSuggestion && styles.isSuggestion,
        section.color == null && styles.isWhite,
        isSelected && styles.isSelected,
        sref.type === 'reference' && styles.isGlobal,
        selectableClassName,
      )}
      style={{
        ['--color' as string]: section.color
          ? SECTION_COLOR_VALUES[section.color]
          : 'white',
      }}
      ref={ref}
      onContextMenu={onContextMenu}
    >
      <NotificationsContainer offset="sitemap">
        {projectEditing && section.mutable && (
          <TaskNotifications
            editing={projectEditing}
            target={section}
            mode={EditorMode.Sitemap}
          />
        )}
      </NotificationsContainer>
      <TextSpecialSectionName>
        <ClickInTextarea
          enableClickIn={isSelected}
          ref={nameRef}
          onChange={sectionEditing?.whenComponentHidden.setName}
          onKeyDown={(e) => {
            const desc = descriptionRef.current;
            if (e.key === 'Enter' && desc) {
              e.preventDefault();
              desc.focus();
            }
            if (e.key === 'Backspace' && e.currentTarget.value.length === 0) {
              e.preventDefault();
              sectionEditing?.deleteWithConfirm();
            }
          }}
          value={section.name}
          placeholder="Section name"
        />
      </TextSpecialSectionName>
      {hasName && ((isSelected && sectionEditing) || hasDescription) && (
        <TextSpecialSectionDescription>
          <ClickInTextarea
            enableClickIn={isSelected}
            ref={descriptionRef}
            onChange={sectionEditing?.whenComponentHidden.setDescription}
            onKeyDown={(e) => {
              if (!sectionEditing) return;
              if (e.key === 'Enter') {
                handoverContinueSelection = sectionEditing.addBelow();
                analytics.track({
                  event: 'blocks/add-section',
                  method: 'blank',
                });
              }
            }}
            value={section.description}
            placeholder="Describe this section"
          />
        </TextSpecialSectionDescription>
      )}
      {menuPortal}
    </div>
  );
});

const DRAG_HANDLE_OPTS: DragHandleOpts = {
  domain: sectionDndDomain,
  draggablePosition: 'parent',
  handlePosition: 'left',
};
